import router from '@/router';
import { RouteLocationNormalized } from 'vue-router';

import { computed } from 'vue';
import { selectedOrgId } from '@/common/utils/role.utils';

import { InviteUser } from '@/types/api/users.types';
import { UserOrganisation } from '@/types/api/auth.types';
import { clearStoresForOrganisationSwitch } from '@/common/utils/stateManagement';

import { useAuthStore } from '@/stores/auth.store';
import { useOrdersStore } from '@/stores/orders.store';
import { useReturnsStore } from '@/stores/returns.store';
import { useOrgStore } from '@/stores/organisations.store';
import { useToastStore } from '@/stores/utils/toast.store';

import i18n from '@/plugins/vue-i18n';
import { analyticsService } from '@/plugins/post-hog';
import { userIdentity } from '@/common/utils/user-identity';
import { landingRouteName } from '@/common/utils/landing.utils';

const { t } = i18n.global;

export const addUsers = (
  usersToAdd: InviteUser[],
  user: InviteUser,
  allowMultiple: boolean = true,
) => {
  const toastStore = useToastStore();
  if (usersToAdd) {
    // Check if there are duplicate email and role combinations
    const duplicateRoleIndex = usersToAdd.findIndex(
      (u) => u.email === user.email && u.roleCode === user.roleCode,
    );

    if (duplicateRoleIndex !== -1) {
      // User already has this exact role
      toastStore.showToast(
        'error',
        t('users.toasts.alreadyAddedRoleEmailCombo.title'),
        t('users.toasts.alreadyAddedRoleEmailCombo.content'),
      );
      return;
    }

    if (!allowMultiple) {
      // Check if user already exists with any role
      const existingUserIndex = usersToAdd.findIndex((u) => u.email === user.email);

      if (existingUserIndex !== -1) {
        // User already exists with a different role
        toastStore.showToast(
          'error',
          t('users.toasts.alreadyAddedOrg.title'),
          t('users.toasts.alreadyAddedOrg.content'),
        );
        return;
      }
    }

    usersToAdd.push(user);
  }
};

interface ApiCallGroup {
  check: () => boolean;
  calls: (item: UserOrganisation) => Promise<void>[];
}

export const setNewOrg = async (item: UserOrganisation) => {
  clearStoresForOrganisationSwitch();

  const orgStore = useOrgStore();
  const authStore = useAuthStore();
  const ordersStore = useOrdersStore();
  const returnStore = useReturnsStore();

  // Set organization before making any API calls
  orgStore.orgChanged = true;
  // Capture the outgoing org id before the switch so analytics can report from→to roles.
  const fromOrgId = authStore.loggedInUser?.selectedOrg?.id;
  await authStore.setSelectedOrg(item);

  analyticsService.trackOrgSwitch(item, fromOrgId);

  try {
    const executeApiCalls = async (apiCallGroups: ApiCallGroup[]) => {
      for (const group of apiCallGroups) {
        if (!group.check()) {
          continue;
        }

        // Context: these prefetches only warm badge counts, but the router.push that ends this
        // try block is what actually completes the switch. Under Promise.all a single refused
        // call — an org-scoped endpoint rejecting a BOQ platform user who holds no role in the
        // org is the live case — aborted the whole block and left the user on their previous
        // page in the new org (PLE-1319).
        const results = await Promise.allSettled(group.calls(item));

        for (const result of results) {
          if (result.status === 'rejected') {
            console.error('Org-switch prefetch failed:', result.reason);
          }
        }
      }
    };

    // Context: the org-admin check this replaced named ORG_ADMIN twice, so an org owner — who
    // outranks an admin — matched neither arm and landed with no accounts-payable prefetch.
    const isOrgAdministrator = computed(() => userIdentity.isOrgAdministrator());
    const isPlatformStaff = computed(() => userIdentity.isPlatformStaff());

    const isRoutingToProjects = !userIdentity.isBareOrgMember() && !userIdentity.isClientAnywhere();

    // API call groups by permissions
    const apiCallGroups: ApiCallGroup[] = [
      // Accounts Payable
      {
        check: () => isOrgAdministrator.value || isPlatformStaff.value,
        calls: () => [
          orgStore.getAccountsPayableTotals({ id: item.id }, true),
          orgStore.getAccountsPayable(item.id, undefined, true),
        ],
      },
      // Projects & Sites — use the canonical navigation gate (includes ORG_FO/ORG_FA and BOQ
      // admins/owners, who see all org projects regardless of project/site assignment). The old
      // hand-rolled role list omitted the org-finance roles, so an FO/FA landed on an empty
      // projects page after an org switch (the view reuses the component and reads the store,
      // which the switch had cleared) until a manual refresh.
      {
        check: () => isRoutingToProjects && userIdentity.seesProjectNavigation(),
        calls: () => [
          orgStore.getOrgProjects(false, { id: item.id, archived: false }),
          orgStore.getProjectManagers(item.id),
        ],
      },
      // Orders, Returns, Claims, Accounts
      {
        // Matches the canonical `canSeeOrders()` navigation audience exactly: the staff-navigation
        // roles plus BOQ platform staff, who are cross-org and hold no role in the org they are
        // switching into. Anyone who can see the badges now gets their counts (PLE-1319).
        check: () => userIdentity.seesStaffNavigation() || userIdentity.isPlatformStaff(),
        calls: () => [
          // Used for badge count in navigation
          orgStore.getPaymentRequestCount(item.id, {
            status: 'Requested',
          }),
          ordersStore.getOrderTotals({ orgId: item.id, type: 'Order' }, 'orders'),
          ordersStore.getOrderTotals({ orgId: item.id, type: 'Claim' }, 'claims'),
          returnStore.getReturnsCount({ organisationId: item.id }),
        ],
      },
    ];

    await executeApiCalls(apiCallGroups);

    // Context: Role capabilities describes the active organisation and is open to every signed-in
    // user, so switching organisation there stays put and shows the viewer's roles in the new one.
    const { name: currentRouteName, hash: currentHash } = router.currentRoute.value;
    if (currentRouteName === 'role-capabilities') {
      await router.push({
        name: 'role-capabilities',
        params: { organisationId: selectedOrgId.value },
        hash: currentHash,
      });
    } else {
      await router.push({
        name: landingRouteName(),
        params: { organisationId: selectedOrgId.value },
      });
    }
  } catch (error) {
    console.error('Error setting new organisation:', error);
  } finally {
    setTimeout(() => (orgStore.orgChanged = false), 500);
  }
};

export async function checkAndSetOrganisation(to: RouteLocationNormalized) {
  const authStore = useAuthStore();
  const orgId = to.params.organisationId as string;

  if (!orgId) {
    return;
  }

  if (orgId !== selectedOrgId.value) {
    const selectedOrg = authStore.loggedInUser?.organisations.find((org) => org.id === orgId);

    if (selectedOrg) {
      await setNewOrg(selectedOrg);
      await router.push(to.fullPath);
    } else {
      await router.push({
        name: 'projects',
        params: { organisationId: selectedOrgId.value },
      });
    }
  }
}
